import type {
  DropdownChangeFn,
  DropdownFilterCallback,
} from '../../components/Dropdown/Dropdown';
import type { RenderMediaGridDataFn } from '../../components/MediaGrid/MediaGrid';
import type {
  ContentObject,
  Media,
} from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import {
  getCtdFeaturedImage,
  getCtdsFromQuery,
  getMediaUrl,
} from '../../lib/flotiq-client/api-helpers';
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../lib/globals';
import { getTestProps, isImagePreviewSupported } from '../../lib/helpers';
import {
  findBorderColor,
  findThumbnail,
  getMediaData,
} from '../../lib/newMediaHelpers';
import { RolePermissions } from '../../lib/rolePermissions';

import Checkbox from '../../components/Checkbox/Checkbox';
import Dropdown from '../../components/Dropdown/Dropdown';
import Heading from '../../components/Heading/Heading';
import Input from '../../components/Input/Input';
import MediaGrid from '../../components/MediaGrid/MediaGrid';
import PickCard from '../../components/PickCard/PickCard';
import StatusPill from '../../components/StatusPill/StatusPill';
import Tooltip from '../../components/Tooltip/Tooltip';

import { useContentType, useContentTypes, useSearch } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useFeaturedImages } from '../../hooks/api/useFeaturedImages';
import useMediaUpload from '../../hooks/api/useMediaUpload';
import useDebounceCallback from '../../hooks/useDebounceCallback';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import UserContext from '../../contexts/UserContext';

import { LinkIcon } from '../../images/shapes';

interface AllObjectsProps<T extends string> {
  /**
   * Limit of an element on page
   */
  limit?: number;
  /**
   * Content type name
   */
  contentType?: T;
  /**
   * Selected objects
   */
  selectedObjects?: Record<string, unknown>;
  /**
   * Callback for selecting object
   */
  selectObject?: (
    object: T extends '_media' ? Media : ContentObject,
    type: string,
  ) => void;
  /**
   * If object filters are in panel
   */
  hasPanel?: boolean;
  /**
   * If the data are displayed in tabs
   */
  isTabs?: boolean;
  /**
   * Page test id
   */
  testId?: string;
}

const AllObjects = <T extends string = ''>({
  limit = 16,
  contentType,
  selectedObjects = EMPTY_OBJECT,
  selectObject = EMPTY_FUNCTION,
  hasPanel = false,
  testId = '',
  isTabs = false,
}: AllObjectsProps<T>) => {
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [type, setType] = useState(contentType);
  const [name, setName] = useState('');
  const [sort, setSort] = useState('_score');
  const [order, setOrder] = useState('desc');
  const { newMedia, removeNewMedia } = useMediaUpload();
  const [currentCtds, setCurrentCtds] = useState<string[]>([]);

  const { permissions } = use(UserContext);

  const COsWithRead = useMemo(
    () =>
      permissions.getCtdNamesWithPermission(
        'CO',
        RolePermissions.PERMISSIONS_TYPES.READ,
      ),
    [permissions],
  );

  const handleTypeChange = useCallback<DropdownChangeFn>((event) => {
    setType(event.target.value);
    setPage(1);
  }, []);

  const setNameFilter = useCallback<DropdownChangeFn>((event) => {
    setName(event.target.value);
    setPage(1);
  }, []);

  const handleNameChange = useDebounceCallback(setNameFilter, 150);

  const params = useMemo(() => {
    return {
      q: name || '*',
      order_by: sort,
      order_direction: order,
      limit,
      page,
      ...(contentType || type ? { content_type: [contentType || type] } : {}),
    };
  }, [name, sort, order, limit, page, contentType, type]);

  const { data, errors, isLoading, pagination } = useSearch(params);

  useEffect(() => {
    if (data.length) {
      const ctds = data
        .map(({ item }) => item?.internal?.contentType)
        .filter((contentType) => contentType);
      setCurrentCtds(ctds);
    }
  }, [data]);

  useEffect(() => {
    if (isLoading) {
      removeNewMedia();
    }
  }, [isLoading, removeNewMedia]);

  const ctdHookOptions = useMemo(
    () => ({
      pause: !!contentType,
    }),
    [contentType],
  );

  const ctdsParams = useMemo(
    () => ({
      limit: 20,
      order_by: 'label',
      order_direction: 'asc',
      ...(COsWithRead ? { names: COsWithRead } : {}),
    }),
    [COsWithRead],
  );

  const {
    data: contentTypes,
    isLoading: contentTypesAreLoading,
    errors: contentTypesErrors,
  } = useContentTypes(ctdsParams, ctdHookOptions);

  const typeHookOptions = useMemo(
    () => ({
      pause: !contentType,
    }),
    [contentType],
  );

  const {
    entity: ctd,
    isLoading: ctdIsLoading,
    errors: ctdErrors,
  } = useContentType(contentType, null, typeHookOptions);

  const [featuredImages, featuredImagesLoading] =
    useFeaturedImages(currentCtds);

  useApiErrorsToast(errors);
  useApiErrorsToast(contentTypesErrors);
  useApiErrorsToast(ctdErrors);

  const ctdOptions = useMemo(() => {
    if (contentType)
      return [{ label: ctd ? ctd.label : contentType, value: contentType }];
    return contentTypes
      .filter(
        (entity) =>
          !entity.internal || ['_media', '_tag'].includes(entity.name),
      )
      .map((entity) => ({ label: entity.label, value: entity.name }));
  }, [contentTypes, ctd, contentType]);

  const filterCtd = useCallback<DropdownFilterCallback>(
    async (query, _, setIsLoading) => {
      setIsLoading(true);
      const newCtds = await getCtdsFromQuery(
        jwt,
        space,
        { ...ctdsParams, label: query },
        t,
      );
      setIsLoading(false);
      return newCtds
        .filter((ctd) => !ctd.internal || ['_media', '_tag'].includes(ctd.name))
        .map((ctd) => ({ label: ctd.label, value: ctd.name }));
    },
    [jwt, space, ctdsParams, t],
  );

  const sortOptions = useMemo(
    () => [
      {
        value: '_score-desc',
        label: t('ContentForm.Relation.Relevance'),
      },
      {
        value: 'internal.createdAt-desc',
        label: t('ContentForm.Relation.DateCreated'),
      },
      {
        value: 'internal.updatedAt-desc',
        label: t('ContentForm.Relation.DateUpdated'),
      },
      {
        value: 'internal.objectTitle.keyword-asc',
        label: t('ContentForm.Relation.TitleAsc'),
      },
      {
        value: 'internal.objectTitle.keyword-desc',
        label: t('ContentForm.Relation.TitleDesc'),
      },
    ],
    [t],
  );

  const handleSortChange = useCallback<DropdownChangeFn>((event) => {
    const [sort, order] = (event.target.value || '-').split('-');
    setSort(sort);
    setOrder(order);
    setPage(1);
  }, []);

  const filtersNode = useMemo(() => {
    return (
      <div className="flex flex-col xs:flex-row xs:flex-wrap sm:flex-nowrap gap-2">
        <Dropdown
          options={ctdOptions}
          value={type}
          onChange={handleTypeChange}
          placeholder={contentType || t('ContentForm.Relation.FilterByType')}
          label={t('ContentForm.Relation.FilterByType')}
          filterCallback={filterCtd}
          debounceTime={500}
          disabled={!!contentType}
          additionalClasses="w-full xs:w-min sm:max-w-52 grow sm:min-w-40 md:min-w-52"
          nullable
          {...getTestProps(testId, 'ctds', 'testId')}
        />
        <Input
          placeholder={t('ContentForm.Relation.SearchBy')}
          type="search"
          onChange={handleNameChange}
          value={name}
          label={t('ContentForm.Relation.Search')}
          additionalClasses="order-last sm:order-0"
          autoFocus
          {...getTestProps(testId, 'name', 'testId')}
        />
        <Dropdown
          options={sortOptions}
          value={`${sort}-${order}`}
          onChange={handleSortChange}
          label={t('ContentForm.Relation.SortBy')}
          additionalClasses="min-w-[170px]"
          {...getTestProps(testId, 'sort', 'testId')}
        />
      </div>
    );
  }, [
    ctdOptions,
    type,
    handleTypeChange,
    contentType,
    t,
    filterCtd,
    testId,
    handleNameChange,
    name,
    sortOptions,
    sort,
    order,
    handleSortChange,
  ]);

  const findTitleField = useCallback(
    (type: string) => {
      if (type === '_media') return 'fileName';
      const contentType = contentTypes.find((ctd) => ctd.name === type);
      if (contentType) {
        const def = contentType.metaDefinition;
        const firstTextField = def.order.find(
          (field) => def.propertiesConfig[field].inputType === 'text',
        );
        return firstTextField || '';
      }
    },
    [contentTypes],
  );

  const renderCard = useCallback<RenderMediaGridDataFn>(
    (data, isNew = false) => {
      const {
        card,
        error = '',
        isLoaded = true,
      } = isNew ? getMediaData(data) : { card: data.item };
      const type = isNew ? '_media' : card.internal.contentType;

      const isImage = card.type === 'image';
      const isMedia = type === '_media';
      const titleField = findTitleField(type);

      const imagePreviewSupported = !isImage
        ? false
        : isImagePreviewSupported(card.extension);

      const getMediaUrlOnSupported = imagePreviewSupported
        ? getMediaUrl(card, 280)
        : '';

      const imageUrl = isMedia
        ? getMediaUrlOnSupported
        : featuredImages[type] || getCtdFeaturedImage(type);

      const onClick = () => selectObject(card, type);

      const extensionThumbnail = isMedia && !imagePreviewSupported && (
        <div className="flex items-center justify-center h-full w-full">
          <div className="flex items-center justify-center rounded-full bg-blue-300 h-12 w-12">
            {card.extension || t('Global.NA')}
          </div>
        </div>
      );

      return (
        <PickCard
          key={card.id}
          title={
            card.internal?.objectTitle || card[titleField || ''] || card.id
          }
          description={isMedia ? 'Media' : type}
          imageUrl={imageUrl}
          imageUrlLoading={isMedia ? false : featuredImagesLoading}
          onClick={!isLoaded || error ? undefined : onClick}
          imageOverlay={
            error || !isLoaded ? (
              ''
            ) : (
              <div className="flex flex-col h-full w-full p-3">
                <div className="flex w-full justify-between">
                  <Checkbox
                    additionalContainerClasses="group-hover:block"
                    additionalCheckboxClasses={twMerge(
                      'border-0 group-hover:block group-hover:border dark:block',
                      'checked:disabled:bg-blue checked:disabled:border-blue disabled:cursor-pointer bg-transparent',
                    )}
                    checked={!!selectedObjects[card.id]}
                    circular
                    {...getTestProps(testId, `${card.id}-cto-card`, 'testId')}
                  />
                  {card.internal?.status && (
                    <StatusPill
                      size="sm"
                      color={card.internal.status}
                      status={t(`ObjectStatus.Status.${card.internal.status}`)}
                      containerClasses="inline-flex items-center justify-center dark:bg-slate-900"
                    />
                  )}
                </div>

                <div className="hidden group-hover:block mt-auto ml-auto">
                  <Tooltip
                    tooltip={t('ObjectsOfType.CopyIdTooltip')}
                    tooltipPlacement="topRight"
                    phoneTooltipPlacement="topRight"
                    additionalClasses="pointer-events-auto"
                  >
                    <CopyToClipboard
                      text={card.id}
                      onCopy={() =>
                        toast.success(
                          t('ObjectsOfType.Copied', { text: card.id }),
                        )
                      }
                      {...getTestProps(testId, 'copy-id')}
                    >
                      <LinkIcon className="w-4 h-4 cursor-copy ml-1 text-sm text-white" />
                    </CopyToClipboard>
                  </Tooltip>
                </div>
              </div>
            )
          }
          thumbnail={
            findThumbnail(error, isLoaded, true, testId) || extensionThumbnail
          }
          additionalContainerClasses={findBorderColor(error, isLoaded, isNew)}
          additionalImageClasses={isMedia ? 'object-scale-down' : ''}
          {...getTestProps(testId, card.id, 'testId')}
        />
      );
    },
    [
      featuredImages,
      featuredImagesLoading,
      findTitleField,
      selectObject,
      selectedObjects,
      t,
      testId,
    ],
  );

  const gridIsLoading = useMemo(() => {
    if (contentType) return isLoading || ctdIsLoading;
    return isLoading || contentTypesAreLoading;
  }, [contentType, isLoading, ctdIsLoading, contentTypesAreLoading]);

  return (
    <MediaGrid
      page={page}
      data={data}
      renderMedia={renderCard}
      isLoading={gridIsLoading}
      errors={errors}
      pagination={pagination}
      handlePageChange={setPage}
      areFiltersApplied={!!(type || name)}
      filtersNode={filtersNode}
      handleNoData={
        <div className="flex justify-center">
          <Heading level={2} color="blue">
            {t('ObjectsOfType.NotFound')}
          </Heading>
        </div>
      }
      hasPanel={hasPanel}
      additionalGridContainerClasses={twMerge(
        isTabs
          ? 'md:max-h-[calc(100vh-28rem)]'
          : 'md:max-h-[calc(100vh-24rem)]',
      )}
      additionalGridClasses={twMerge(
        'h-full md:max-h-[calc(100vh-28rem)] px-2',
        'gap-5! grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]!',
      )}
      newMedia={contentType ? [] : newMedia}
      emptyFilterText={t('ObjectsOfType.NotFound')}
      testId={testId}
    />
  );
};

export default AllObjects;
